import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import InputAdornment from "@material-ui/core/InputAdornment";
import MenuItem from "@material-ui/core/MenuItem";

import SearchIcon from "@material-ui/icons/Search";
import SyncIcon from "@material-ui/icons/Sync";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import FindInPageOutlinedIcon from "@material-ui/icons/FindInPageOutlined";

import MainContainer from "../../components/MainContainer";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import MetaTemplateEditor from "../../components/MetaTemplateEditor";
import { PageHeader, EmptyState } from "../../components/ui";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { useDate } from "../../hooks/useDate";
import { getTokens } from "../../styles/tokens";
import StatusChip from "./StatusChip";

const OFFICIAL_CHANNEL = "whatsapp_oficial";
const SEARCH_DEBOUNCE_MS = 500;

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    filters: {
      flex: "none",
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: 12,
      marginBottom: 16,
    },
    search: {
      width: "min(320px, 100%)",
      [theme.breakpoints.down("xs")]: { width: "100%" },
    },
    searchIcon: { color: t.textMuted },
    connSelect: {
      width: 280,
      maxWidth: "100%",
      [theme.breakpoints.down("xs")]: { width: "100%" },
    },
    // "Conexão oficial" vai dentro do campo, antes do nome: sem rótulo flutuante
    selectValue: {
      display: "flex",
      alignItems: "baseline",
      gap: 6,
      minWidth: 0,
    },
    selectPrefix: { flex: "none", fontSize: 13, color: t.textMuted },
    selectName: { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    // O cartão rola (a paginação é por scroll): encolhe até o conteúdo e para na altura da página
    card: {
      flex: "0 1 auto",
      minHeight: 0,
      overflow: "auto",
      boxShadow: t.shadow.card,
      ...theme.scrollbarStyles,
    },
    table: { minWidth: 760 },
    th: { whiteSpace: "nowrap", position: "sticky", top: 0, zIndex: 1 },
    name: {
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
      fontSize: 13,
      overflowWrap: "anywhere",
    },
    cellNowrap: { whiteSpace: "nowrap" },
    rowAction: { color: t.textSecondary },
    rowActionDanger: { color: t.semantic.error },
    btnWrapper: { position: "relative" },
    btnProgress: {
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: -10,
      marginLeft: -10,
    },
  };
});

// 1ª frase vira o título do estado vazio; o resto, a mensagem.
const splitSentence = (text) => {
  const str = String(text || "");
  const i = str.indexOf(". ");
  return i < 0 ? [str, ""] : [str.slice(0, i + 1), str.slice(i + 2)];
};

const tr = (key, opts) => i18n.t(`metaTemplates.${key}`, opts);

const MetaTemplates = () => {
  const classes = useStyles();
  const history = useHistory();
  const { datetimeToClient } = useDate();

  const [whatsapps, setWhatsapps] = useState([]);
  const [loadingWhatsapps, setLoadingWhatsapps] = useState(true);
  const [whatsappId, setWhatsappId] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchParam, setSearchParam] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Conexões oficiais
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/whatsapp");
        const official = (Array.isArray(data) ? data : []).filter(
          (w) => String(w.channel || "").toLowerCase() === OFFICIAL_CHANNEL
        );
        if (!active) return;
        setWhatsapps(official);
        if (official.length) setWhatsappId(official[0].id);
      } catch (err) {
        toastError(err);
      } finally {
        if (active) setLoadingWhatsapps(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Busca com debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchParam(searchInput.trim());
      setPageNumber(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const reload = useCallback(() => {
    setPageNumber(1);
    setReloadKey((k) => k + 1);
  }, []);

  // Lista paginada no servidor
  useEffect(() => {
    if (!whatsappId) return undefined;
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/meta-templates", {
          params: { whatsappId, pageNumber, searchParam },
        });
        if (!active) return;
        const list = Array.isArray(data?.templates) ? data.templates : [];
        setTemplates((prev) => (pageNumber === 1 ? list : [...prev, ...list]));
        setHasMore(!!data?.hasMore);
      } catch (err) {
        if (active) toastError(err);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [whatsappId, pageNumber, searchParam, reloadKey]);

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      setPageNumber((p) => p + 1);
    }
  };

  const handleChangeWhatsapp = (e) => {
    setWhatsappId(e.target.value);
    setTemplates([]);
    setPageNumber(1);
  };

  const handleSync = async () => {
    if (!whatsappId) return;
    setSyncing(true);
    try {
      const { data } = await api.post(`/meta-templates/sync/${whatsappId}`);
      toast.success(tr("toasts.synced", { total: Number(data?.synced) || 0 }));
      reload();
    } catch (err) {
      toastError(err);
    } finally {
      if (isMountedRef.current) setSyncing(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setEditorOpen(true);
  };

  const openEdit = async (item) => {
    try {
      const { data } = await api.get(`/meta-templates/${item.id}`);
      if (!isMountedRef.current) return;
      setEditing(data || item);
    } catch (err) {
      setEditing(item);
    }
    setEditorOpen(true);
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/meta-templates/${deleting.id}`);
      toast.success(tr("toasts.deleted"));
      setTemplates((prev) => prev.filter((x) => x.id !== deleting.id));
    } catch (err) {
      toastError(err);
    } finally {
      setDeleting(null);
    }
  };

  const labelFor = (group, value) => {
    const key = `metaTemplates.${group}.${value}`;
    const out = i18n.t(key);
    return out === key ? value || "-" : out;
  };

  const noOfficial = !loadingWhatsapps && whatsapps.length === 0;
  const [emptyTitle, emptyMessage] = splitSentence(tr("empty.noTemplates"));

  const renderConnection = (value) => {
    const current = whatsapps.find((w) => String(w.id) === String(value));
    return (
      <span className={classes.selectValue}>
        <span className={classes.selectPrefix}>{tr("list.connection")}</span>
        <span className={classes.selectName}>{current ? current.name : ""}</span>
      </span>
    );
  };

  return (
    <MainContainer maxWidth>
      <ConfirmationModal
        title={deleting ? tr("confirm.deleteTitle", { name: deleting.name }) : ""}
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
      >
        {tr("confirm.deleteMessage")}
      </ConfirmationModal>

      <MetaTemplateEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        template={editing}
        whatsappId={whatsappId}
        whatsapps={whatsapps}
        onSaved={reload}
      />

      <PageHeader
        title={tr("title")}
        actions={
          <>
            <div className={classes.btnWrapper}>
              <Button
                variant="outlined"
                startIcon={<SyncIcon />}
                onClick={handleSync}
                disabled={noOfficial || syncing || !whatsappId}
              >
                {tr("buttons.sync")}
              </Button>
              {syncing && <CircularProgress size={20} className={classes.btnProgress} />}
            </div>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={openNew}
              disabled={noOfficial || !whatsappId}
            >
              {tr("buttons.new")}
            </Button>
          </>
        }
      />

      {!noOfficial && (
        <div className={classes.filters}>
          <TextField
            placeholder={tr("list.search")}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            variant="outlined"
            size="small"
            className={classes.search}
            inputProps={{ "aria-label": tr("list.search") }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" className={classes.searchIcon} />
                </InputAdornment>
              ),
            }}
          />
          {whatsapps.length > 1 && (
            <TextField
              select
              variant="outlined"
              size="small"
              className={classes.connSelect}
              value={whatsappId}
              onChange={handleChangeWhatsapp}
              SelectProps={{ renderValue: renderConnection }}
            >
              {whatsapps.map((w) => (
                <MenuItem key={w.id} value={w.id}>
                  {w.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        </div>
      )}

      <Paper variant="outlined" className={classes.card} onScroll={handleScroll}>
        {noOfficial ? (
          <EmptyState
            icon={<WhatsAppIcon />}
            title={tr("empty.noConnectionTitle")}
            message={tr("empty.noConnection")}
            action={
              <Button variant="outlined" onClick={() => history.push("/connections")}>
                {tr("empty.goToConnections")}
              </Button>
            }
          />
        ) : (
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.th}>{tr("table.name")}</TableCell>
                <TableCell className={classes.th}>{tr("table.category")}</TableCell>
                <TableCell className={classes.th}>{tr("table.language")}</TableCell>
                <TableCell className={classes.th}>{tr("table.status")}</TableCell>
                <TableCell className={classes.th}>{tr("table.updatedAt")}</TableCell>
                <TableCell className={classes.th} align="right">
                  {tr("table.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className={classes.name}>{item.name}</TableCell>
                  <TableCell className={classes.cellNowrap}>
                    {labelFor("category", item.category)}
                  </TableCell>
                  <TableCell className={classes.cellNowrap}>
                    {labelFor("language", item.language)}
                  </TableCell>
                  <TableCell>
                    <StatusChip status={item.status} reason={item.metaRejectedReason} />
                  </TableCell>
                  <TableCell className={classes.cellNowrap}>
                    {item.updatedAt ? datetimeToClient(item.updatedAt) : "-"}
                  </TableCell>
                  <TableCell align="right" className={classes.cellNowrap}>
                    <Tooltip title={tr("buttons.edit")}>
                      <IconButton
                        size="small"
                        className={classes.rowAction}
                        onClick={() => openEdit(item)}
                        aria-label={tr("buttons.edit")}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={tr("buttons.delete")}>
                      <IconButton
                        size="small"
                        className={classes.rowActionDanger}
                        onClick={() => setDeleting(item)}
                        aria-label={tr("buttons.delete")}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {(loading || loadingWhatsapps) && <TableRowSkeleton columns={6} />}
              {!loading && !loadingWhatsapps && templates.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} style={{ borderBottom: "none" }}>
                    {searchParam ? (
                      <EmptyState icon={<FindInPageOutlinedIcon />} title={tr("empty.noResults")} />
                    ) : (
                      <EmptyState
                        icon={<DescriptionOutlinedIcon />}
                        title={emptyTitle}
                        message={emptyMessage}
                        action={
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={openNew}
                            disabled={!whatsappId}
                          >
                            {tr("buttons.new")}
                          </Button>
                        }
                      />
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>
    </MainContainer>
  );
};

export default MetaTemplates;
